

import { QueueLockedEventStatus, QueueStatus, ResponseStatus, UpdateStatusEvent, UpdateStatusEventType } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import { HubConnection } from "@microsoft/signalr/dist/esm";
import { useCallback, useContext, useMemo, useState } from "react";
import useSignalROnMessage from "../api/useSignalROnMessage";

export const useLockedEventStatusUpdate = (onQueued?: (event: UpdateStatusEvent) => Promise<void>, onUpdate?: (event: UpdateStatusEvent) => Promise<void>) => {

    const client = useContext(ServiceStackClientContext);

    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<ResponseStatus | undefined>(undefined);

    const serverEventOnMessage = useMemo(() => {
        if (!onUpdate && !onQueued) {
            return undefined;
        }

        return (connection: HubConnection, data: UpdateStatusEvent) => {
            if (onQueued && data && data.type === UpdateStatusEventType.Queued) {
                onQueued(data);
            }

            if (onUpdate && data && data.type === UpdateStatusEventType.Complete) {
                onUpdate(data);
            }
        }
    }, [onUpdate, onQueued])

    useSignalROnMessage<UpdateStatusEvent>(AppSettings.channels.updateStatus, serverEventOnMessage);

    const queueUpdate = useCallback(async (query: Partial<QueueStatus>) => {
        setProcessing(true);
        setError(undefined);

        try {
            await client.post(new QueueLockedEventStatus(query));
        }
        catch (err: any) {
            setError(err.responseStatus)
        }

        setProcessing(false);
    }, [client, setProcessing, setError]);

    const result = useMemo(() => ({
        processing,
        error,
        queueUpdate
    }), [processing, error, queueUpdate])

    return result;
}

export default useLockedEventStatusUpdate;